import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Seo from "@/components/Seo";
import { Loader2, Mail, ChevronDown } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useLang } from "@/context/LangContext";
import { LangToggle } from "@/components/LangToggle";
import { ThemeToggle } from "@/components/ThemeToggle";
import { BrandHome } from "@/components/BrandHome";
import { GoogleButton, goGoogle, InAppWarning } from "@/pages/Register";

export default function Login() {
  const { user, login, formatError } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = (params.get("next") || "").startsWith("/app") ? params.get("next") : "";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [manual, setManual] = useState(false);
  const [loading, setLoading] = useState(false);
  const submitted = useRef(false);
  useEffect(() => {
    if (user && !submitted.current) navigate(next || "/app", { replace: true });
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e) => {
    e.preventDefault();
    submitted.current = true;
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success(t("مرحباً بعودتك 👋", "Welcome back 👋"));
      navigate(next || (u?.role === "admin" ? "/app/admin-overview" : "/app"), { replace: true });
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || t("فشل تسجيل الدخول", "Login failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid-bg flex items-center justify-center px-4 py-8">
      <Seo title="تسجيل الدخول" description="سجّل الدخول إلى منصة رواج الذكية عبر Google أو البريد الإلكتروني." path="/login" />
      <div className="w-full max-w-md">
        <div className="flex items-center justify-between mb-3 px-1">
          <BrandHome />
          <div className="flex items-center gap-2"><LangToggle className="h-8" /><ThemeToggle /></div>
        </div>
      <div className="glass rounded-3xl p-6 sm:p-8 emerald-glow animate-float-up relative">
        <h1 className="text-2xl font-bold text-center mb-1 mt-1">{t("تسجيل الدخول", "Log in")}</h1>
        <p className="text-muted-foreground text-center text-sm mb-6">{t("ادخل بحساب Google بضغطة واحدة", "One tap with your Google account")}</p>
        {/* REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
        <InAppWarning />
        <GoogleButton label={t("الدخول عبر Google", "Continue with Google")} testId="google-login-btn" onClick={() => goGoogle(false)} />
        <div className="flex items-center gap-3 my-5">
          <div className="flex-1 h-px bg-border" /><span className="text-xs text-muted-foreground">{t("أو", "or")}</span><div className="flex-1 h-px bg-border" />
        </div>
        <button type="button" onClick={() => setManual((m) => !m)} className="w-full flex items-center justify-center gap-2 text-sm text-primary font-semibold" data-testid="login-manual-toggle">
          <Mail className="w-4 h-4" /> {t("الدخول بالبريد وكلمة المرور", "Email & password")} <ChevronDown className={`w-4 h-4 transition-transform ${manual ? "rotate-180" : ""}`} />
        </button>
        {manual && (
          <form onSubmit={submit} className="space-y-3 mt-4" data-testid="login-manual-form">
            <div>
              <Label className="mb-1.5 block text-xs">{t("البريد الإلكتروني", "Email")}</Label>
              <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" dir="ltr" className="bg-secondary border-border text-start" data-testid="login-email-input" />
            </div>
            <div>
              <Label className="mb-1.5 block text-xs">{t("كلمة المرور", "Password")}</Label>
              <Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" dir="ltr" className="bg-secondary border-border text-start" data-testid="login-password-input" />
            </div>
            <Button type="submit" disabled={loading} className="w-full bg-primary hover:bg-primary/90 text-white font-bold h-11" data-testid="auth-login-submit-btn">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : t("دخول", "Log in")}
            </Button>
          </form>
        )}
        <p className="text-center text-sm text-muted-foreground mt-6">
          {t("ليس لديك حساب؟", "No account yet?")}{" "}
          <Link to={next ? `/register?next=${encodeURIComponent(next)}` : "/register"} className="text-primary font-semibold hover:underline" data-testid="go-register">
            {t("أنشئ حساباً جديداً", "Create a new account")}
          </Link>
        </p>
      </div>
      </div>
    </div>
  );
}
